import { useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Link2 } from "lucide-react";
import { LienFichier, Provenance } from "@/composants/accuses-reception";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { dateSemaine, horodatage } from "@/lib/libelles";
import {
  api,
  type CommandeDuDocument,
  type CommandeFournisseur,
  type DocumentEnPartie,
  type DocumentEntrant,
  type EtatDocumentEntrant,
} from "@/lib/api";

const ETATS: { valeur: EtatDocumentEntrant; libelle: string }[] = [
  { valeur: "A_TRAITER", libelle: "À traiter" },
  { valeur: "RATTACHE", libelle: "Rattachés" },
  { valeur: "ECARTE", libelle: "Écartés" },
];

const ETAT_LISIBLE: Record<EtatDocumentEntrant, string> = {
  A_TRAITER: "À traiter",
  RATTACHE: "Rattaché",
  ECARTE: "Écarté",
};

/**
 * La file des documents entrants (LOT 2, ticket #20, ADR-0040) : les pièces
 * relevées dans la boîte des AR que LABGEST n'a rapprochées d'aucune commande.
 * Seule « Déposer et rattacher les documents fournisseurs » l'ouvre (ADR-0049) ;
 * l'entrée du menu est masquée aux autres, et le serveur les refuse quoi qu'il en soit.
 *
 * Rattacher fait du document l'import d'AR de la commande choisie, qui attend
 * la validation de qui agit sur la commande client. Écarter demande un motif.
 * Rien ne se supprime : les documents traités restent lisibles, par état.
 *
 * Ticket #41 : un document commun que chacun rattache pour sa part attend ici,
 * « en partie rattaché », que le reste de ses commandes le reçoive ; il se
 * complète depuis l'une d'elles.
 */
export function PageDocumentsEntrants() {
  const [etat, setEtat] = useState<EtatDocumentEntrant>("A_TRAITER");
  const file = useChargement((signal) => api.documentsEntrants(etat, signal), [etat]);
  const enPartie = useChargement((signal) => api.documentsEnPartie(signal), []);
  // Les commandes qui attendent un AR : celles où l'on peut rattacher.
  const commandes = useChargement(
    (signal) => api.commandesFournisseurs({ attendent_un_ar: true, limite: 200 }, signal),
    [],
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Documents entrants</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Les pièces reçues dans la boîte des AR que LABGEST n'a rapprochées d'aucune commande
          fournisseur. Rattachez-les à leur commande, ou écartez-les avec un motif.
        </p>
      </div>

      {enPartie.erreur ? (
        <Erreur message={enPartie.erreur} onReessayer={enPartie.recharger} />
      ) : null}
      {enPartie.donnees && enPartie.donnees.length > 0 ? (
        <section className="flex flex-col gap-3" aria-labelledby="titre-en-partie">
          <h2 id="titre-en-partie" className="text-base font-semibold text-encre">
            {enPartie.donnees.length === 1
              ? "1 document commun en partie rattaché"
              : `${enPartie.donnees.length} documents communs en partie rattachés`}
          </h2>
          <ul className="flex flex-col gap-3">
            {enPartie.donnees.map((document) => (
              <li key={document.import_ocr_id}>
                <DocumentCommun document={document} onClos={enPartie.recharger} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="max-w-xs">
        <ChampSelection
          libelle="Afficher"
          value={etat}
          options={ETATS}
          onChange={(evenement) => setEtat(evenement.target.value as EtatDocumentEntrant)}
        />
      </div>

      {file.erreur ? <Erreur message={file.erreur} onReessayer={file.recharger} /> : null}
      {!file.erreur && file.enCours ? <Chargement message="Chargement de la file…" /> : null}
      {!file.enCours && file.donnees?.length === 0 ? (
        <Vide
          titre={
            etat === "A_TRAITER"
              ? "Aucun document à traiter"
              : `Aucun document ${ETAT_LISIBLE[etat].toLowerCase()}`
          }
          detail={
            etat === "A_TRAITER"
              ? "Tout ce que la boîte des AR a relevé a trouvé sa commande, ou a été traité."
              : undefined
          }
        />
      ) : null}

      {!file.enCours && file.donnees && file.donnees.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {file.donnees.map((entrant) => (
            <li key={entrant.id}>
              <EntreeDeFile
                entrant={entrant}
                commandes={commandes.donnees?.elements ?? []}
                onTraite={file.recharger}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function libelleCommandeDuDocument(commande: CommandeDuDocument) {
  const numeros = commande.numeros_commandes_clients.join(", ");
  return `${numeros ? `${numeros}, ` : ""}commande du ${dateSemaine(commande.date_commande)}${
    commande.reference_fournisseur ? ` (réf. ${commande.reference_fournisseur})` : ""
  }`;
}

/**
 * Ticket #41 : un document commun, rattaché pour une part, qui attend le reste.
 * Quand le reste ne viendra pas, on le clôt ; ses frais communs se saisissent
 * alors depuis l'une de ses commandes.
 */
function DocumentCommun({ document, onClos }: { document: DocumentEnPartie; onClos: () => void }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  return (
    <Carte>
      <CorpsCarte className="flex flex-col gap-2 text-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 font-medium text-encre">
              {document.nom_fichier ?? "Document sans nom"}
              <Etiquette ton="alerte">En partie rattaché</Etiquette>
            </p>
            <p className="text-encre-douce">
              {document.fournisseur} · reçu le {horodatage(document.importe_le)}
            </p>
            {document.courriel ? <Provenance courriel={document.courriel} /> : null}
          </div>
          {document.document_id ? (
            <LienFichier
              documentId={document.document_id}
              libelle={`Télécharger le document commun de ${document.fournisseur}`}
            />
          ) : null}
        </div>
        <p className="text-encre">
          Déjà rattaché à :{" "}
          {document.rattachees.length > 0
            ? document.rattachees.map(libelleCommandeDuDocument).join(" ; ")
            : "des commandes que vous ne voyez pas"}
          .
        </p>
        {document.a_rattacher.length > 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-encre-douce">
              Le reste est peut-être parmi les commandes de ce fournisseur qui attendent un AR.
              Complétez-le depuis l'une d'elles :
            </p>
            <ul className="flex flex-col gap-1">
              {document.a_rattacher.map((commande) => (
                <li key={commande.commande_fournisseur_id}>
                  <Link
                    to={`/commandes-fournisseurs/${commande.commande_fournisseur_id}`}
                    className="text-laiton-fonce underline underline-offset-2"
                  >
                    {libelleCommandeDuDocument(commande)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-encre-douce">
            Aucune autre commande de ce fournisseur n'attend d'AR.
          </p>
        )}
        <span>
          <Bouton
            type="button"
            taille="compact"
            variante="discret"
            disabled={envoi}
            onClick={async () => {
              setEnvoi(true);
              setErreur(null);
              try {
                await api.validerImportOcr(document.import_ocr_id, {
                  date_ar: document.importe_le.slice(0, 10),
                  parts: [],
                  reste_a_rattacher: false,
                });
                onClos();
              } catch (cause) {
                setErreur(messageDe(cause));
              }
              setEnvoi(false);
            }}
          >
            Le reste ne viendra pas : le clore
          </Bouton>
        </span>
        {erreur ? (
          <p role="alert" className="text-alerte">
            {erreur}
          </p>
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}

function EntreeDeFile({
  entrant,
  commandes,
  onTraite,
}: {
  entrant: DocumentEntrant;
  commandes: CommandeFournisseur[];
  onTraite: () => void;
}) {
  const [geste, setGeste] = useState<"rattacher" | "ecarter" | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const agir = async (appel: () => Promise<unknown>) => {
    setErreur(null);
    try {
      await appel();
      setGeste(null);
      onTraite();
    } catch (cause) {
      setErreur(messageDe(cause));
    }
  };

  return (
    <Carte>
      <CorpsCarte className="flex flex-col gap-2 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex flex-wrap items-center gap-2 text-encre">
            <Etiquette ton={entrant.etat === "A_TRAITER" ? "actif" : "neutre"}>
              {ETAT_LISIBLE[entrant.etat]}
            </Etiquette>
            <span>
              Reçu le {horodatage(entrant.courriel.recu_le)}
              {entrant.nom_fichier ? ` : ${entrant.nom_fichier}` : ""}
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {entrant.document_id ? (
              <LienFichier
                documentId={entrant.document_id}
                libelle={`Télécharger ${entrant.nom_fichier ?? "le fichier"}`}
              />
            ) : null}
            {entrant.etat === "A_TRAITER" && geste === null ? (
              <>
                <Bouton taille="compact" onClick={() => setGeste("rattacher")}>
                  <Link2 className="h-3.5 w-3.5" aria-hidden />
                  Rattacher
                </Bouton>
                <Bouton taille="compact" variante="discret" onClick={() => setGeste("ecarter")}>
                  <Ban className="h-3.5 w-3.5" aria-hidden />
                  Écarter
                </Bouton>
              </>
            ) : null}
          </div>
        </div>
        <Provenance courriel={entrant.courriel} />
        <Traitement entrant={entrant} />
        {erreur ? (
          <p role="alert" className="text-alerte">
            {erreur}
          </p>
        ) : null}
        {geste === "rattacher" ? (
          <FormulaireRattachement
            commandes={commandes}
            onAnnuler={() => setGeste(null)}
            onRattacher={(commandeId) =>
              agir(() => api.rattacherDocumentEntrant(entrant.id, commandeId))
            }
          />
        ) : null}
        {geste === "ecarter" ? (
          <FormulaireEcart
            onAnnuler={() => setGeste(null)}
            onEcarter={(motif) => agir(() => api.ecarterDocumentEntrant(entrant.id, motif))}
          />
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}

/** Ce qu'on en a fait, qui et quand, en toutes lettres. */
function Traitement({ entrant }: { entrant: DocumentEntrant }) {
  const quand = `${entrant.traite_par ?? "Quelqu'un"}, le ${horodatage(entrant.traite_le)}`;
  if (entrant.etat === "RATTACHE") {
    return (
      <p className="text-encre-douce">
        Rattaché par {quand}.{" "}
        {entrant.commande_fournisseur_id ? (
          <Link
            to={`/commandes-fournisseurs/${entrant.commande_fournisseur_id}`}
            className="font-medium text-encre underline"
          >
            Ouvrir la commande fournisseur
          </Link>
        ) : null}
      </p>
    );
  }
  if (entrant.etat === "ECARTE") {
    return (
      <p className="break-words text-encre-douce">
        Écarté par {quand}, motif : « {entrant.motif_ecart} »
      </p>
    );
  }
  return null;
}

function libelleCommande(commande: CommandeFournisseur) {
  const reference = commande.reference_fournisseur ? `, réf. ${commande.reference_fournisseur}` : "";
  const numeros = commande.commandes_clients.map((servie) => servie.numero_commande_client);
  return `${commande.fournisseur.nom}, ${numeros.length > 1 ? "commandes client" : "commande client"} ${numeros.join(", ")}${reference}`;
}

function FormulaireRattachement({
  commandes,
  onAnnuler,
  onRattacher,
}: {
  commandes: CommandeFournisseur[];
  onAnnuler: () => void;
  onRattacher: (commandeId: string) => Promise<void>;
}) {
  const [commandeId, setCommandeId] = useState("");
  const [envoi, setEnvoi] = useState(false);
  if (commandes.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-encre-douce">Aucune commande fournisseur n'attend d'AR.</p>
        <Bouton type="button" taille="compact" variante="discret" onClick={onAnnuler}>
          Fermer
        </Bouton>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onRattacher(commandeId);
        setEnvoi(false);
      }}
    >
      <ChampSelection
        libelle="Commande fournisseur"
        aide="Celles qui attendent encore un AR. Le document y attendra la validation de qui suit la Commande / Projet."
        required
        value={commandeId}
        onChange={(evenement) => setCommandeId(evenement.target.value)}
        options={[
          { valeur: "", libelle: "Choisir une commande…" },
          ...commandes.map((commande) => ({
            valeur: commande.id,
            libelle: libelleCommande(commande),
          })),
        ]}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !commandeId}>
          Rattacher à cette commande
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function FormulaireEcart({
  onAnnuler,
  onEcarter,
}: {
  onAnnuler: () => void;
  onEcarter: (motif: string) => Promise<void>;
}) {
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onEcarter(motif.trim());
        setEnvoi(false);
      }}
    >
      <Champ
        libelle="Motif de l'écart"
        aide="Une publicité, une relance… Le document reste gardé, avec ce motif."
        required
        value={motif}
        onChange={(evenement) => setMotif(evenement.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !motif.trim()}>
          Écarter le document
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
